<template>
    <div class="image-message-container">
        <div class="header-message">
            <p>Image Message ({{ templateOrder + 1 }}/3)</p>
            <div @click="removeImageTemplate">
                <i class="bx bx-trash"></i>
            </div>
        </div>
        <ValidationProvider
          :value="imageFile"
          :rules="isImageFromCopiedMessage ? null : {
            required: {
              value: true,
              message: 'Please upload an image',
            },
            multiCustom: {
              callbacks: [isImageTypeValid, isImageSizeValid],
              messages: [
                'The file upload must be in JPG, JPEG, or PNG format',
                `The maximum file upload size is ${maxImageSize} MB`,
              ],
            }
          }"
          v-slot="{ errors }"
          ref="imageValidationProviderRef"
        >
        <div v-if="isUploadingImage" class="uploading-container">
          <div class="loading-section">
            <div class="spinner-border" role="status"></div>
          </div>
        </div>
        <div v-else class="message-content">
            <div v-if="thumbnailImage === null" class="dropzone-container">
                <vue-dropzone
                    ref="imageDropzoneRef"
                    id="image-dropzone"
                    :options="dropzoneOptions"
                    :useCustomSlot=true
                    @vdropzone-file-added="handleUploadingImage"
                    @vdropzone-thumbnail="handleThumbnail"
                >
                <div class="dropzone-custom-input">
                    <div>
                        <i class="bx bx-image"></i>
                    </div>
                    <p class="dropzone-custom-title">Drag an image here<br/>or <span class="text-highlight">Upload a file</span></p>
                    <p class="text-subtitle">File formats: JPG, JPEG, PNG<br/>File size: Up to 10 MB</p>
                </div>
                </vue-dropzone>
            </div>
            <div v-else class="thumbnail-container" style="position: relative;">
                <img :src="thumbnailImage" alt="thumbnail"/>
                <div
                @click="removeThumbnailImage"
                class="thumbnail-remove"
                >
                    <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                </div>
            </div>
            <div class="error-container">
              <p class="error-message">
                  {{ errors[0] }}
              </p>
            </div>
          </div>
        </ValidationProvider>
    </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { ValidationProvider } from 'vee-validate';
import aliceApi from '../../../../alice-api';

export default {
  name: 'ImageMessage',
  components: {
    VueDropzone,
    ValidationProvider,
  },
  props: {
    templateOrder: {
      type: Number,
      required: true,
    },
    copiedImageMessage: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      dropzoneOptions: {
        url: '/',
        maxFilesize: 10, // MB
        acceptedFiles: 'image/jpeg,image/png,image/jpg',
        maxFiles: 1,
        thumbnailWidth: 250,
        thumbnailHeight: 250,
        autoProcessQueue: false,
      },
      thumbnailImage: null,
      maxImageSize: 10, // MB,
      isUploadingImage: false,
      imageFile: null,
      isImageFromCopiedMessage: false,
    };
  },
  mounted() {
    if (this.copiedImageMessage && Object.keys(this.copiedImageMessage).length > 0) {
      this.loadCopiedImageMessage();
    } else {
      this.$refs.imageValidationProviderRef.validate(this.imageFile);
    }
  },
  methods: {
    removeImageTemplate() {
      this.thumbnailImage = null;
      this.$emit('remove-message-template');
    },
    async handleUploadingImage(file) {
      this.isUploadingImage = true;
      this.imageFile = file;
      const { valid } = await this.$refs.imageValidationProviderRef.validate(file);
      if (!valid) {
        this.thumbnailImage = null;
        this.isUploadingImage = false;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'IMAGE');
      const response = await this.sendUploadedImage(formData);
      if (response) {
        this.$emit('update-image-message', response.data.data);
        this.thumbnailImage = URL.createObjectURL(file);
      }
      this.isUploadingImage = false;
    },
    isImageSizeValid(file) {
      if (!file) return false;
      const maxSizeBytes = this.maxImageSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    isImageTypeValid(file) {
      if (!file) return false;
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      return acceptedTypes.includes(file.type);
    },
    async sendUploadedImage(formData) {
      try {
        const response = await aliceApi.uploadMediaMessage(formData);
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      }
    },
    loadCopiedImageMessage () {
      this.thumbnailImage = this.copiedImageMessage.filePath;
      this.isImageFromCopiedMessage = true;
      this.$emit('update-image-message', this.copiedImageMessage);
    },
    handleThumbnail(file, dataURL) {
      this.thumbnailImage = dataURL;
    },
    async removeThumbnailImage() {
      this.isImageFromCopiedMessage = false;
      await this.$nextTick();
      this.thumbnailImage = null;
      this.imageFile = null;
      this.$refs.imageValidationProviderRef.validate(null);
      this.$emit('remove-image-message');
    },
  },
};
</script>

<style lang="scss" scoped>
.image-message-container {
    margin-top: 20px;
    border: 1px solid #DADCE4;
    border-radius: 8px;
    p{
        margin-bottom: 0px;
    }
    .header-message{
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #F5F6FA;
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0px;
        color: #0D173C;
        padding: 10px 16px;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid #DADCE4;
        i{
            font-size: 16px;
            color: #4B5178;
            cursor: pointer;
        }
    }
    .message-content{
        padding: 20px;
        .dropzone-container{
            width: 250px;
            height: 250px;
            #image-dropzone{
                width: 100%;
                height: 100%;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        margin-bottom: 19px;
                    }
                    .dropzone-custom-title{
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 120%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #4B5178;
                        margin-bottom: 24px;
                        .text-highlight{
                            color: #4848FF;
                        }
                    }
                    .text-subtitle{
                        font-weight: 400;
                        font-size: 12px;
                        line-height: 150%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #868BA6;
                    }
                }
                // .dz-image {
                //     width: 100%;
                //     height: 100%;
                //     img {
                //         width: 100%;
                //         height: 100%;
                //         object-fit: cover;
                //     }
                // }
                // .dz-remove {
                //     display: none;
                // }
            }
            // #image-dropzone .dz-preview {
            // /* Your custom styles */
            // background-color: #f7f7f7;
            // border-radius: 8px;
            // margin: 10px;
            // }

            // #image-dropzone .dz-preview .dz-image {
            // border-radius: 8px;
            // width: 150px;
            // height: 150px;
            // }

            // /* More specific selectors for other elements inside preview */
            // #image-dropzone .dz-preview .dz-details {
            // padding: 10px;
            // }
        }
        .thumbnail-container{
            width: 250px;
            height: 250px;
            border-radius: 8px;
            overflow: hidden;
            img{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .thumbnail-remove{
                position: absolute;
                top: 10px;
                right: 10px;
                width: 30px;
                height: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #0D173C7A;
                border-radius: 50%;
                cursor: pointer;
            }
        }
        .error-message{
            font-weight: 400;
            font-size: 12px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #EF6767;
            height: 18px;
            width: max-content;
            padding-top: 10px;
        }
        .error-container{
          height: 28px;
        }
    }
    .uploading-container{
      padding: 20px;
      .loading-section{
          display: flex;
          justify-content: center;
          align-items: center;
          width: 250px;
          height: 250px;
      }
    }
}
</style>
